import { FC, ReactNode, useCallback, createContext, useMemo, useRef } from 'react';

import { DataLayerEntry } from '..';
import { GTM, Props as GTMProps } from './GTM';
import { getDataLayerEnvironment } from './utils/getEnvironment';

type GTMContextType = {
  pushToDataLayer: (event: DataLayerEntry) => void;
  pushInitialDataLayerEvents: (events: DataLayerEntry[]) => void;
  getIsInitialized: () => boolean;
  setIsInitialized: (value: boolean) => void;
};

export const GTMContext = createContext<GTMContextType | null>(null);

const getDataLayer = () => {
  if (typeof window === 'undefined') return null;
  window.dataLayer = window.dataLayer || ([] as DataLayerEntry[]);
  return window.dataLayer;
};

export const legacyGtmSupportProvider = {
  pushToDataLayer: (event: DataLayerEntry) => {
    getDataLayer()?.push({
      ...event,
      environment: getDataLayerEnvironment(),
    });
  },
  pushInitialDataLayerEvents: (events: DataLayerEntry[]) => {
    events.forEach(event =>
      getDataLayer()?.push({
        ...event,
        environment: getDataLayerEnvironment(),
      }),
    );
  },
  getIsInitialized: () => true,
  setIsInitialized: () => {},
};

export const GTMProvider: FC<GTMProps & { children: ReactNode; useLegacy?: boolean }> = ({
  children,
  useLegacy,
  ...props
}) => {
  const isInitializedRef = useRef(false);
  const queuedEventsRef = useRef<DataLayerEntry[]>([]);

  const dataLayer = getDataLayer();

  const getIsInitialized = useCallback(() => isInitializedRef.current, []);
  const setIsInitialized = useCallback((value: boolean) => {
    isInitializedRef.current = value;
  }, []);

  const pushToDataLayer = useCallback(
    (event: DataLayerEntry) => {
      const payload = {
        ...event,
        environment: getDataLayerEnvironment(),
      };
      if (!getIsInitialized()) {
        queuedEventsRef.current.push(payload);
        return;
      }
      dataLayer?.push(payload);
    },
    [getIsInitialized, dataLayer],
  );

  const pushInitialDataLayerEvents = useCallback(
    (events: DataLayerEntry[]) => {
      events.forEach(event => dataLayer?.push(event));
      queuedEventsRef.current.forEach(event => dataLayer?.push(event));
      queuedEventsRef.current = [];
      setIsInitialized(true);
    },
    [dataLayer, setIsInitialized],
  );

  const value = useMemo(
    () => ({
      pushToDataLayer,
      pushInitialDataLayerEvents,
      getIsInitialized,
      setIsInitialized,
    }),
    [pushToDataLayer, pushInitialDataLayerEvents, getIsInitialized, setIsInitialized],
  );

  return (
    <GTMContext.Provider value={useLegacy ? legacyGtmSupportProvider : value}>
      <>
        {!useLegacy && <GTM {...props} />}
        {children}
      </>
    </GTMContext.Provider>
  );
};

export const GTMTestProvider: FC<Partial<GTMContextType> & { children: ReactNode }> = ({
  children,
  ...contextFunctions
}) => {
  const value = {
    ...legacyGtmSupportProvider,
    ...contextFunctions,
  };
  return <GTMContext.Provider value={value}>{children}</GTMContext.Provider>;
};
